/**
 * Slow auto-advancing carousel for the marketing homepage.
 *
 * The track is a plain scroll-snap container, so it still works without JS and
 * with a trackpad; this element only adds the timed advance and the controls.
 *
 *   <yc-carousel data-interval="6000">
 *     <div data-track> <div data-slide>…</div> … </div>
 *     <button data-prev> <button data-next>
 *     <div data-dots></div>
 *   </yc-carousel>
 */
class YcCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
    if (this.slides.length < 2) return;

    this.interval = Number(this.dataset.interval) || 6000;
    this.index = 0;
    this.paused = false;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.#step(-1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#step(1, true));

    this.#refreshDots();

    /* The element upgrades before the section's stylesheet has applied, when
       the track is not yet a scroll container and every slide looks off-screen.
       Measuring then gives a dot per slide on a carousel that does not scroll,
       so the count is taken after first paint and again whenever the track is
       resized - which is also what makes it follow the responsive slide count. */
    requestAnimationFrame(() => this.#refreshDots());
    this.resizeObserver = new ResizeObserver(() => this.#refreshDots());
    this.resizeObserver.observe(this.track);

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerenter', () => { this.paused = true; });
    this.addEventListener('pointerleave', () => { this.paused = false; });
    this.addEventListener('focusin', () => { this.paused = true; });
    this.addEventListener('focusout', () => { this.paused = false; });

    // A carousel that keeps moving off-screen is wasted work and a battery cost.
    this.observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.#start() : this.#stop()),
      { threshold: 0.2 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  #start() {
    if (this.timer) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => {
      if (!this.paused) this.#step(1, false);
    }, this.interval);
  }

  #stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  #step(delta, fromUser) {
    const last = this.#lastIndex();
    let next = this.index + delta;
    if (next > last) next = 0;
    if (next < 0) next = last;
    this.#goTo(next, fromUser);
  }

  #goTo(index, fromUser) {
    const slide = this.slides[index];
    if (!slide) return;
    this.index = index;
    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: fromUser === false && this.#reduced() ? 'auto' : 'smooth',
    });
    this.#syncDots();
  }

  #reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * With several slides visible at once the track runs out of scroll before the
   * last slide reaches the left edge, so stopping at slides.length - 1 would
   * leave the carousel stuck on a position it can never reach.
   */
  #lastIndex() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    for (let i = 0; i < this.slides.length; i++) {
      if (this.slides[i].offsetLeft - this.track.offsetLeft >= maxScroll - 2) return i;
    }
    return this.slides.length - 1;
  }

  #onScroll = () => {
    clearTimeout(this.scrollEnd);
    this.scrollEnd = setTimeout(() => {
      const left = this.track.scrollLeft + this.track.offsetLeft;
      let closest = 0;
      let best = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < best) { best = distance; closest = i; }
      });
      this.index = closest;
      this.#syncDots();
    }, 120);
  };

  #refreshDots() {
    this.dotsHost ??= this.querySelector('[data-dots]');
    if (!this.dotsHost) return;

    const stops = this.#lastIndex() + 1;

    // Every slide is already on screen, so there is nothing to page between.
    if (stops < 2) {
      this.dotsHost.hidden = true;
      this.dotsHost.replaceChildren();
      this.dots = [];
      return;
    }

    this.dotsHost.hidden = false;
    if (this.dots?.length === stops) {
      this.#syncDots();
      return;
    }

    this.dotsHost.replaceChildren();
    this.dots = Array.from({ length: stops }, (_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'yc-carousel__dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.#goTo(i, true));
      this.dotsHost.append(dot);
      return dot;
    });
    this.#syncDots();
  }

  #syncDots() {
    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('yc-carousel')) customElements.define('yc-carousel', YcCarousel);
